import { z } from 'zod';

/**
 * The PURE extraction contract for the clinical-tasks agent: the flat draft
 * schema + category/priority vocabulary. Shared browser/backend (like
 * `@eluve/plan-shapes`), so the backend extraction, the review pipeline, and
 * the frontend panel all read ONE definition — no mirrored copies to drift.
 * Must stay free of Medplum / NestJS / FHIR-build imports.
 */

// `patient-instruction` was dropped (PRD v1 excludes patient-facing tasks —
// extraction is clinician/care-team actions only). Stored tasks carrying the
// old code degrade to the generic "Task" label; the code is never offered or
// extracted again.
export const CLINICAL_TASK_CATEGORIES = [
  'lab-order',
  'referral',
  'follow-up',
  'medication',
  'general',
] as const;

export type ClinicalTaskCategory = (typeof CLINICAL_TASK_CATEGORIES)[number];

/** What a write stores for a task that carries no category. The write contract
 *  types the field non-nullable, so every path backfills it — declared once so
 *  the history diff can recognise the backfill and not bill it to a clinician. */
export const CLINICAL_TASK_DEFAULT_CATEGORY: ClinicalTaskCategory = 'general';

/** Human-facing label per category — used for Task.code.text and review chips. */
export const CLINICAL_TASK_CATEGORY_LABELS: Record<
  ClinicalTaskCategory,
  string
> = {
  'lab-order': 'Lab order',
  referral: 'Referral',
  'follow-up': 'Follow-up',
  medication: 'Medication',
  general: 'General',
};

/** Chip label for any (possibly unknown) stored category code. */
export const categoryLabel = (category: string | null): string =>
  category && category in CLINICAL_TASK_CATEGORY_LABELS
    ? CLINICAL_TASK_CATEGORY_LABELS[category as ClinicalTaskCategory]
    : 'Task';

// FHIR request-priority codes (Task.priority), highest first. Surfaced to
// clinicians as P1–P4.
export const CLINICAL_TASK_PRIORITIES = [
  'stat',
  'asap',
  'urgent',
  'routine',
] as const;

export type ClinicalTaskPriority = (typeof CLINICAL_TASK_PRIORITIES)[number];

/** What a write stores for a task that carries no priority — see
 *  {@link CLINICAL_TASK_DEFAULT_CATEGORY}. */
export const CLINICAL_TASK_DEFAULT_PRIORITY: ClinicalTaskPriority = 'routine';

/** Human-facing label per priority — the P-scale shown on review chips. */
export const CLINICAL_TASK_PRIORITY_LABELS: Record<
  ClinicalTaskPriority,
  string
> = {
  stat: 'P1 · Highest',
  asap: 'P2 · High',
  urgent: 'P3 · Medium',
  routine: 'P4 · Low',
};

/** Narrow any (possibly unknown) stored priority code, or null. */
export const asPriority = (
  value: string | null,
): ClinicalTaskPriority | null =>
  value && (CLINICAL_TASK_PRIORITIES as readonly string[]).includes(value)
    ? (value as ClinicalTaskPriority)
    : null;

/**
 * One extracted Clinical Task — deliberately FLAT (design decision, ENG-5218):
 * category as an enum, free-text description, optional due date. No typed
 * per-category payloads.
 */
export const clinicalTaskDraftSchema = z.object({
  category: z
    .enum(CLINICAL_TASK_CATEGORIES)
    .describe(
      'The kind of clinician/care-team follow-up action: lab-order (order/perform a lab test), referral (refer to another provider/specialist), follow-up (book or schedule a future visit/check-in), medication (start/change/renew a medication), general (any other clinician/care-team action).',
    ),
  description: z
    .string()
    .min(1)
    .describe(
      'The action to perform, as one imperative sentence, e.g. "Order a complete blood count" or "Refer to orthopedic surgeon for the left shoulder".',
    ),
  priority: z
    .enum(CLINICAL_TASK_PRIORITIES)
    .describe(
      'Clinical urgency, using FHIR request-priority: stat (P1 — same day / immediately), asap (P2 — within days), urgent (P3 — soon, within a few weeks), routine (P4 — standard scheduling). Pick routine unless the source signals urgency.',
    ),
  dueDate: z
    .string()
    .nullable()
    .describe(
      'ISO-8601 date (YYYY-MM-DD) the action should be done by, ONLY if the source states or clearly implies a timeframe; null otherwise. Never invent a date.',
    ),
  supportingPassage: z
    .string()
    .nullable()
    .describe(
      'The verbatim sentence(s) from the NOTE or TRANSCRIPT that support this task. Quote exactly; null only if nothing supports it.',
    ),
  confidence: z
    .number()
    .min(0)
    .max(1)
    .describe(
      'Your confidence this task is correct and supported, from 0 (guess) to 1 (certain).',
    ),
});

export type ClinicalTaskDraft = z.infer<typeof clinicalTaskDraftSchema>;
